import type { GearSix } from "@phosphor-icons/react";
import { Label } from "@/components/ui/label";
import type { SettingsStatusTone } from "@/lib/therapist-settings-status";
import { cn } from "@/lib/utils";

export function SectionShell({
  title,
  description,
  children,
  footer,
}: {
  icon?: typeof GearSix;
  title: string;
  description: string;
  children: React.ReactNode;
  footer?: React.ReactNode;
}) {
  return (
    <section className="rounded-2xl border border-border/60 bg-card px-5 py-5 shadow-sm sm:px-7 sm:py-6">
      <header className="mb-5">
        <h2
          className="text-2xl font-semibold leading-tight tracking-tight text-foreground"
          style={{ fontFamily: "var(--font-heading)" }}
        >
          {title}
        </h2>
        <p className="mt-1.5 max-w-2xl text-sm leading-relaxed text-muted-foreground">{description}</p>
      </header>
      <div>{children}</div>
      {footer && <div className="mt-6 flex justify-end border-t border-border/60 pt-5">{footer}</div>}
    </section>
  );
}

/**
 * A single minimalist settings row: label on the left, control on the right,
 * divided by a thin border. Use `block` for wide controls (textarea, chips,
 * lists) that should span the full width beneath the label.
 */
export function SettingRow({
  label,
  description,
  htmlFor,
  children,
  block = false,
  alignTop = false,
}: {
  label: string;
  description?: string;
  htmlFor?: string;
  children: React.ReactNode;
  block?: boolean;
  alignTop?: boolean;
}) {
  return (
    <div
      className={cn(
        "border-b border-border/60 py-3.5 first:pt-0.5 last:border-b-0 last:pb-0.5",
        !block && "sm:grid sm:grid-cols-[minmax(0,1fr)_minmax(0,340px)] sm:gap-6",
        !block && (alignTop ? "sm:items-start" : "sm:items-center"),
      )}
    >
      <div className={cn(block && "mb-2")}>
        <Label htmlFor={htmlFor} className="text-sm font-medium text-foreground">
          {label}
        </Label>
        {description && <p className="mt-0.5 text-xs leading-relaxed text-muted-foreground">{description}</p>}
      </div>
      <div className={cn("w-full", !block && "mt-1.5 sm:mt-0")}>{children}</div>
    </div>
  );
}

export function FieldGroup({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section className="grid gap-4 border-t border-border/60 pt-5 first:border-t-0 first:pt-0 lg:grid-cols-[150px_minmax(0,1fr)]">
      <div>
        <h3 className="text-xs font-semibold uppercase tracking-[0.14em] text-primary">{title}</h3>
      </div>
      <div className="space-y-4">{children}</div>
    </section>
  );
}

export function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="space-y-2">
      <Label>{label}</Label>
      {children}
    </div>
  );
}

export function MiniMetric({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex min-h-28 flex-col justify-center bg-card px-4 py-4 text-center">
      <p className="text-2xl font-semibold tabular-nums text-foreground" style={{ fontFamily: "var(--font-heading)" }}>
        {value}
      </p>
      <p className="mt-0.5 text-[10px] font-medium uppercase tracking-[0.16em] text-muted-foreground">{label}</p>
    </div>
  );
}

export function StatusPill({
  tone,
  label,
  className,
}: {
  tone: SettingsStatusTone;
  label: string;
  className?: string;
}) {
  const c = toneClasses(tone);
  return (
    <span
      className={cn(
        "inline-flex w-fit items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-semibold ring-1",
        c.pill,
        className,
      )}
    >
      <span className={cn("size-1.5 rounded-full", c.dot)} />
      {label}
    </span>
  );
}

export function toneClasses(tone: SettingsStatusTone) {
  if (tone === "ready") {
    return {
      pill: "bg-emerald-50 text-emerald-700 ring-emerald-200",
      dot: "bg-emerald-500",
      soft: "bg-emerald-50 text-emerald-700 ring-1 ring-emerald-200",
    };
  }
  if (tone === "blocked") {
    return {
      pill: "bg-rose-50 text-rose-700 ring-rose-200",
      dot: "bg-rose-500",
      soft: "bg-rose-50 text-rose-700 ring-1 ring-rose-200",
    };
  }
  if (tone === "warning") {
    return {
      pill: "bg-amber-50 text-amber-700 ring-amber-200",
      dot: "bg-amber-500",
      soft: "bg-amber-50 text-amber-700 ring-1 ring-amber-200",
    };
  }
  return {
    pill: "bg-muted text-muted-foreground ring-border",
    dot: "bg-muted-foreground",
    soft: "bg-muted text-muted-foreground ring-1 ring-border",
  };
}
